import { MappedCategoryModel } from "@api-types";
import { GamingLimitIncreaseConsentLog } from "@api-types/account-settings/GamingLimitIncreaseConsentLog";
import {
	LimitCategoriesViewModel,
	LimitCategoryTypes,
	LimitTypeItem,
	LimitTypes,
	ResolveDepositAndLossLimit,
	ResolveLimit,
	ResolveLimitRequest,
	UserLimit,
} from "@data-types";
import { LimitDurationDataType } from "@data-types/membership/registration";
import { MyLimitsService } from "@services/account-settings/MyLimitsService";
import { localizationService } from "@state";
import { LoaderStore } from "@state/stores/common";
import { action, computed, observable, runInAction } from "mobx";

import { AccountTypes, SGUID_EMPTY_CONST } from "@utils";
import { DateTime } from "luxon";
import type { RootStore } from "@state/stores/types";
import { LIMITS_CONSTANTS } from "@services/account-settings/MyLimitsConstants";
import {
	getLinkedCategory,
	getTypeFromCategoryAbrv,
} from "@utils/account-settings/my-limits/getCategoryAbrv";

export default class MyLimitsStore {
	loader: LoaderStore;
	rootStore: RootStore;
	constructor(rootStore: RootStore) {
		this.activeAccount = rootStore.userAuthStore.isInternetUser
			? AccountTypes.ONLINE
			: AccountTypes.OFFLINE;
		this.rootStore = rootStore;
		this.loader = new LoaderStore();
	}
	//#region  observable

	@observable lookups: {
		limitDurations: LimitDurationDataType[];
		limitTypes: LimitTypeItem[];
	} | null = null;

	@observable activeAccount: AccountTypes;

	@observable tabs: MappedCategoryModel[] | null = null;
	@observable category: LimitCategoriesViewModel | null = null;

	@observable startLossLimitValue: number = 0;
	@observable isFetchingAdditionalCategory: boolean = false;
	@observable submitNonce: number = 0;

	@observable activeConsentLogs: GamingLimitIncreaseConsentLog[] = [];
	@observable isWithdrawConfirmationOpen: boolean = false;

	private lastFetchedLimitId: string | undefined = undefined;

	//#endregion  observable

	@computed get getSingleLimitDurations(): UserLimit {
		const placeholder: UserLimit = {
			limitId: "",
			durationId: "",
			name: "",
			canBeUpdated: false,
			canBeDeleted: false,
			categoryAbrv: "",
			abrv: "",
			units: 0,
			amount: null,
			gamingLimitTypeAbrv: "",
			gamingLimitTypeName: "",
			maxValue: LIMITS_CONSTANTS.GENERAL_MAX_AMOUNT,
			cooldownDate: null,
		};
		if (!this.category) return placeholder;
		if (!this.category.durations) {
			return placeholder;
		}
		if (this.category.durations.length < 1) {
			return placeholder;
		}
		return this.category.durations[0];
	}

	@computed get isMultiModeEnabled() {
		return this.category?.isMultiTypeMode || false;
	}

	@computed get isRequestPending() {
		return this.loader.isLoadingProcess;
	}

	@computed get canWithdrawConsent(): boolean {
		return this.activeConsentLogs.length > 0;
	}

	@computed get withdrawGamingLimitId(): string | undefined {
		return this.activeConsentLogs[0]?.gamingLimitId;
	}

	@computed get consentedAtDisplay(): string | null {
		const dateCreated = this.activeConsentLogs[0]?.dateCreated;
		return dateCreated
			? DateTime.fromISO(dateCreated).toFormat("dd.MM.yyyy HH:mm:ss")
			: null;
	}

	@action.bound
	setStarterLossLimitValue(value: number) {
		this.startLossLimitValue = value;
	}

	@action.bound
	openWithdrawConfirmation(): void {
		this.isWithdrawConfirmationOpen = true;
	}

	@action.bound
	closeWithdrawConfirmation(): void {
		this.isWithdrawConfirmationOpen = false;
	}

	@action.bound
	setActiveAccount(account: AccountTypes): void {
		this.activeAccount = account;
	}

	@action.bound
	setCategory(category: LimitCategoriesViewModel): void {
		this.category = category;
		this.closeWithdrawConfirmation();
	}

	@action.bound
	setIsFetchingAdditionalCategory(value: boolean): void {
		this.isFetchingAdditionalCategory = value;
	}

	@action.bound
	async onInitialize() {
		this.loader.suspend();
		try {
			const response = await MyLimitsService.getTabs();
			runInAction(() => {
				this.tabs = response.tabs;
			});
		} catch (error) {
			console.error(error);
			const errorMessage = localizationService.t(
				error?.data?.message || "error-handling:GENERIC_ERROR"
			);
			this.rootStore.notificationStore.error(errorMessage);
		}
		this.loader.resume();
	}

	private getMonthlyLimitId(
		category: LimitCategoriesViewModel | null
	): string | undefined {
		const id = category?.durations?.find(
			(d) => d.units == LIMITS_CONSTANTS.MONTHLY_DURATION_UNITS
		)?.limitId;
		return id && id !== SGUID_EMPTY_CONST ? id : undefined;
	}

	private async resolveMonthlyDepositLimitId(
		categoryAbrv: string | undefined,
		category: LimitCategoriesViewModel | null
	): Promise<string | undefined> {
		if (!categoryAbrv) {
			return undefined;
		}
		if (
			getTypeFromCategoryAbrv(categoryAbrv) === LimitCategoryTypes.GAMES
		) {
			return undefined;
		}
		const depositCategoryAbrv = getLinkedCategory(
			getTypeFromCategoryAbrv(categoryAbrv),
			LimitTypes.DEPOSIT
		);
		if (categoryAbrv === depositCategoryAbrv) {
			return this.getMonthlyLimitId(category);
		}
		const monthlyDeposit =
			await this.fetchLimitInfoFromNotSelectedCategory(
				depositCategoryAbrv
			);
		const id = monthlyDeposit?.limitId;
		return id && id !== SGUID_EMPTY_CONST ? id : undefined;
	}

	@action.bound
	async fetchLastConsent(limitId?: string, force: boolean = false) {
		if (!force && limitId === this.lastFetchedLimitId) {
			return;
		}
		this.lastFetchedLimitId = limitId;
		if (!limitId) {
			runInAction(() => {
				this.activeConsentLogs = [];
			});
			return;
		}
		try {
			const consents = await MyLimitsService.fetchLastConsent(
				this.activeAccount,
				limitId
			);
			runInAction(() => {
				this.activeConsentLogs = consents.filter(
					(c) => c.withdrawnAt == null
				);
			});
		} catch (error) {
			console.error(error);
		}
	}

	@action.bound
	async withdrawConsent() {
		if (this.isRequestPending) {
			return;
		}
		const gamingLimitId = this.withdrawGamingLimitId;
		if (!gamingLimitId) {
			return;
		}
		this.loader.suspend();
		try {
			await MyLimitsService.withdrawConsent(
				this.activeAccount,
				gamingLimitId
			);
			if (this.category?.activeCategoryAbrv) {
				await this.fetchLimitInfo(this.category.activeCategoryAbrv);
			}
			await this.fetchLastConsent(
				await this.resolveMonthlyDepositLimitId(
					this.category?.activeCategoryAbrv,
					this.category
				),
				true
			);
			runInAction(() => {
				this.rootStore.notificationStore.success(
					localizationService.t("my-limits:CONSENT_WITHDRAW_SUCCESS")
				);
				this.closeWithdrawConfirmation();
			});
		} catch (error) {
			console.error(error);
			this.rootStore.notificationStore.error(
				localizationService.t("my-limits:CONSENT_WITHDRAW_ERROR")
			);
		}
		this.loader.resume();
	}

	@action.bound
	async fetchLimitInfo(categoryAbrv: string) {
		this.loader.suspend();

		try {
			const response = await MyLimitsService.getTabData(
				this.activeAccount,
				categoryAbrv
			);

			if (!response.isMultiLimitModeEnabled) {
				const lookup = await MyLimitsService.fetchLimitLookups();
				runInAction(() => {
					this.lookups = lookup;
				});
			}
			runInAction(() => {
				this.setCategory(response);
			});
			await this.fetchLastConsent(
				await this.resolveMonthlyDepositLimitId(categoryAbrv, response)
			);
		} catch (error) {
			console.error(error);
			const errorMessage = localizationService.t(
				"error-handling:" + (error?.error?.message || "GENERIC_ERROR")
			);
			this.rootStore.notificationStore.error(errorMessage);
		}
		this.loader.resume();
	}

	async fetchLimitInfoFromNotSelectedCategory(categoryAbrv: string) {
		this.setIsFetchingAdditionalCategory(true);

		try {
			const response = await MyLimitsService.getTabData(
				this.activeAccount,
				categoryAbrv
			);

			const monthlyDuration = response.durations.find(
				(limit) => limit.units == 30
			);
			return monthlyDuration;
		} catch (err) {
			console.error(err);
		} finally {
			this.setIsFetchingAdditionalCategory(false);
		}
	}

	@action.bound
	async submitRequestLimit(model: ResolveLimitRequest) {
		this.loader.suspend();
		try {
			const response = await MyLimitsService.resolveRequest(
				this.activeAccount,
				model
			);
			if (response !== undefined) {
				runInAction(() => {
					this.setCategory(response);
					this.rootStore.notificationStore.success(
						response?.infoMessage || "error-handling:GENERIC_ERROR"
					);
				});
			}
		} catch (error) {
			console.error(error);
			const errorMessage = localizationService.t(
				error?.message || "COMMON.LIMITS." + error
			);
			this.rootStore.notificationStore.error(errorMessage);
		}
		this.loader.resume();
	}

	@action.bound
	async submitLimit(model: ResolveLimit) {
		this.loader.suspend();
		try {
			const response = await MyLimitsService.resolveLimit(
				this.activeAccount,
				model
			);
			if (response) {
				runInAction(() => {
					this.setCategory(response);
					this.rootStore.notificationStore.success(
						response?.infoMessage || "error-handling:GENERIC_ERROR"
					);
				});
			}
		} catch (error) {
			console.error(error);
			if (error?.errorCode === 20) {
				this.rootStore.accountVerificationStore.showPopup(
					"account-settings/account-verification",
					"ACCOUNT_VERIFICATION.TITLE",
					localizationService.t(
						"ACCOUNT_VERIFICATION.ACC_VERIFICATION_NAVIGATION"
					),
					false
				);
			} else if (error?.errorCode === 21) {
				this.rootStore.accountVerificationStore.showPopup(
					"limit-increase",
					"LIMIT_INCREASE.TITLE",
					localizationService.t(
						"LIMIT_INCREASE.LIMIT_INCREASE_NAVIGATION"
					),
					false
				);
			} else if (error?.errorCode === 400242) {
				const errorMessage = localizationService.t(
					"MEMBERSHIP.ERR_HANDLING.LUGAS_VALIDATION_ERROR"
				);
				this.rootStore.notificationStore.error(errorMessage);
			} else if (error?.errorCode === 400244) {
				const errorMessage = localizationService.t(
					"COMMON.LIMITS.LUGAS_LIMIT_EXCEEDS_MAX"
				);
				this.rootStore.notificationStore.error(errorMessage);
			} else if (error?.errorCode === 400245) {
				const errorMessage = localizationService.t(
					"MEMBERSHIP.ERR_HANDLING.LUGAS_DOWN"
				);
				this.rootStore.notificationStore.error(errorMessage);
			} else if (error?.errorCode === 400256) {
				const errorMessage = localizationService.t(
					"MEMBERSHIP.ERR_HANDLING.LUGAS_UNSPECIFIED"
				);
				this.rootStore.notificationStore.error(errorMessage);
			} else {
				const errorMessage = localizationService.t(
					error?.message || "COMMON.LIMITS." + error
				);
				this.rootStore.notificationStore.error(errorMessage);
			}
		}
		this.loader.resume();
	}

	@action.bound
	async submitMonthlyDepositLimitWithLoss(model: ResolveDepositAndLossLimit) {
		this.loader.suspend();
		try {
			const response = await MyLimitsService.resolveDepositLimitWithLoss(
				this.activeAccount,
				model
			);
			if (response) {
				runInAction(() => {
					this.setCategory(response);
					this.submitNonce++;
					this.rootStore.notificationStore.success(
						response?.infoMessage || "error-handling:GENERIC_ERROR"
					);
				});
				await this.fetchLastConsent(
					await this.resolveMonthlyDepositLimitId(
						response.activeCategoryAbrv,
						response
					),
					true
				);
			}
		} catch (error) {
			console.error(error);
			if (error?.errorCode === 20) {
				this.rootStore.accountVerificationStore.showPopup(
					"account-settings/account-verification",
					"ACCOUNT_VERIFICATION.TITLE",
					localizationService.t(
						"ACCOUNT_VERIFICATION.ACC_VERIFICATION_NAVIGATION"
					),
					false
				);
			} else if (error?.errorCode === 21) {
				this.rootStore.accountVerificationStore.showPopup(
					"limit-increase",
					"LIMIT_INCREASE.TITLE",
					localizationService.t(
						"LIMIT_INCREASE.LIMIT_INCREASE_NAVIGATION"
					),
					false
				);
			} else if (error?.errorCode === 400242) {
				const errorMessage = localizationService.t(
					"MEMBERSHIP.ERR_HANDLING.LUGAS_VALIDATION_ERROR"
				);
				this.rootStore.notificationStore.error(errorMessage);
			} else if (error?.errorCode === 400244) {
				const errorMessage = localizationService.t(
					"COMMON.LIMITS.LUGAS_LIMIT_EXCEEDS_MAX"
				);
				this.rootStore.notificationStore.error(errorMessage);
			} else if (error?.errorCode === 400245) {
				const errorMessage = localizationService.t(
					"MEMBERSHIP.ERR_HANDLING.LUGAS_DOWN"
				);
				this.rootStore.notificationStore.error(errorMessage);
			} else if (error?.errorCode === 400256) {
				const errorMessage = localizationService.t(
					"MEMBERSHIP.ERR_HANDLING.LUGAS_UNSPECIFIED"
				);
				this.rootStore.notificationStore.error(errorMessage);
			} else if (error?.errorCode === 35) {
				this.rootStore.notificationStore.error(error?.message);
			} else {
				const errorMessage = localizationService.t(
					error?.message || "COMMON.LIMITS." + error
				);
				this.rootStore.notificationStore.error(errorMessage);
			}
		}
		this.loader.resume();
	}
}

export enum LimitRequestAction {
	REMOVE = 1,
	APPLY = 2,
}

export type SubmitLimit = {
	amount: number;
	action: number;
	categoryId: string;
	durationId: string;
	typeId: string;
	limitId: string;
};

export enum SubmitLimitAction {
	NONE = 0,
	ADD = 1,
	REMOVE = 2,
	UPDATE = 3,
}

export type MyLimitsStoreCategoriesNullChecked = Omit<
	MyLimitsStore,
	"category"
> & { category: LimitCategoriesViewModel };

export type MyLimitsStoreTabsNullChecked = Omit<MyLimitsStore, "tabs"> & {
	tabs: MappedCategoryModel[];
};

export const timeLimitsDropDown = [
	{
		id: 30,
		name: "specific:USER.ACCOUNT_SETTINGS.MY_LIMITS.TIME_INTERVAL.INTERVAL_30_MINUTES",
	},
	{
		id: 60,
		name: "specific:USER.ACCOUNT_SETTINGS.MY_LIMITS.TIME_INTERVAL.INTERVAL_1_HOUR",
	},
	{
		id: 120,
		name: "specific:USER.ACCOUNT_SETTINGS.MY_LIMITS.TIME_INTERVAL.INTERVAL_2_HOURS",
	},
	{
		id: 180,
		name: "specific:USER.ACCOUNT_SETTINGS.MY_LIMITS.TIME_INTERVAL.INTERVAL_3_HOURS",
	},
	{
		id: 240,
		name: "specific:USER.ACCOUNT_SETTINGS.MY_LIMITS.TIME_INTERVAL.INTERVAL_4_HOURS",
	},
	{
		id: 300,
		name: "specific:USER.ACCOUNT_SETTINGS.MY_LIMITS.TIME_INTERVAL.INTERVAL_5_HOURS",
	},
	{
		id: 360,
		name: "specific:USER.ACCOUNT_SETTINGS.MY_LIMITS.TIME_INTERVAL.INTERVAL_6_HOURS",
	},
	{
		id: 480,
		name: "specific:USER.ACCOUNT_SETTINGS.MY_LIMITS.TIME_INTERVAL.INTERVAL_8_HOURS",
	},
	{
		id: 600,
		name: "specific:USER.ACCOUNT_SETTINGS.MY_LIMITS.TIME_INTERVAL.INTERVAL_10_HOURS",
	},
	{
		id: 720,
		name: "specific:USER.ACCOUNT_SETTINGS.MY_LIMITS.TIME_INTERVAL.INTERVAL_12_HOURS",
	},
];
